// src/hooks/useShipping.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ShippingService } from '../admin/pages/shipping/shippingService';
import { ShippingCountry } from '../admin/pages/shipping/shipping';

export const SHIPPING_KEYS = {
  all: ['shipping'] as const,
  lists: () => ['shipping', 'list'] as const,
  paginated: (page: number, search: string) =>
    ['shipping', 'list', page, search] as const,
  stats: () => ['shipping', 'stats'] as const,
};

// FIX: Removed the `cursors` field — seek-based pagination only needs `page`.
export interface ShippingPaginationParams {
  page: number;
  search: string;
  pageSize: number;
  // `cursors` intentionally removed — seek-based pagination uses only `page`
}

// FIX: Passes `params.page` directly to getPaginated instead of cursor
// objects. Any page can now be fetched in any order.
export const useShippingPaginated = (params: ShippingPaginationParams) => {
  return useQuery({
    queryKey: SHIPPING_KEYS.paginated(params.page, params.search),
    queryFn: () =>
      ShippingService.getPaginated(
        params.pageSize,
        params.page,
        params.search || undefined
      ),
    staleTime: 1000 * 60 * 5,
    placeholderData: (prev: any) => prev,
  });
};

export const useShippingStats = () => {
  return useQuery({
    queryKey: SHIPPING_KEYS.stats(),
    queryFn: () => ShippingService.getStats(),
    staleTime: 1000 * 60 * 5,
  });
};

export const useShippingMutations = (userId: string) => {
  const queryClient = useQueryClient();

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: SHIPPING_KEYS.all });
  };

  const create = useMutation({
    mutationFn: (country: ShippingCountry) => ShippingService.create(country, userId),
    onSuccess: invalidateAll,
  });

  const update = useMutation({
    mutationFn: ({ id, country }: { id: string; country: ShippingCountry }) =>
      ShippingService.update(id, country),
    onSuccess: invalidateAll,
  });

  const remove = useMutation({
    mutationFn: (id: string) => ShippingService.delete(id),
    onSuccess: invalidateAll,
  });

  return { create, update, remove };
};